import { ChevronDown } from 'lucide-react';
import { RSVP_RESPONSES, type RsvpResponseValue } from '@peles/shared';
import { DropdownMenu } from '../../common/DropdownMenu';

const TONE_CLASSES: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  danger: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  neutral: 'bg-gray-100 text-gray-700',
};

const DOT_CLASSES: Record<string, string> = {
  success: 'bg-green-500',
  danger: 'bg-red-500',
  warning: 'bg-amber-500',
  neutral: 'bg-gray-400',
};

export function rsvpOption(response: RsvpResponseValue | undefined) {
  return RSVP_RESPONSES.find((o) => o.value === response) ?? RSVP_RESPONSES[3];
}

/** An answer as a coloured chip — the same look on every guest screen. */
export function RsvpChip({ response }: { response: RsvpResponseValue | undefined }) {
  const option = rsvpOption(response);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${TONE_CLASSES[option.tone]}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${DOT_CLASSES[option.tone]}`} aria-hidden />
      {option.label}
    </span>
  );
}

export interface RsvpMenuProps {
  response: RsvpResponseValue | undefined;
  /** Called with the answer chosen. Absent: the chip is shown, not offered. */
  onChange?: (response: RsvpResponseValue) => void;
  /** Names the guest, for the menu's accessible label. */
  guestName: string;
  disabled?: boolean;
}

/**
 * A guest's answer, changed by choosing from a menu — never by tapping the
 * chip round a cycle, which let a stray click change somebody's RSVP.
 */
export function RsvpMenu({ response, onChange, guestName, disabled }: RsvpMenuProps) {
  if (!onChange) return <RsvpChip response={response} />;
  return (
    <DropdownMenu
      ariaLabel={`RSVP for ${guestName}`}
      menuClassName="w-48"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          disabled={disabled}
          aria-label={`RSVP for ${guestName}: ${rsvpOption(response).label}`}
          className="inline-flex items-center gap-0.5 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 disabled:opacity-60"
        >
          <RsvpChip response={response} />
          <ChevronDown size={14} className="text-gray-400" aria-hidden />
        </button>
      )}
      items={RSVP_RESPONSES.map((option) => ({
        id: option.value,
        label: option.label,
        selected: option.value === (response ?? 'PENDING'),
        onSelect: () => {
          if (option.value !== (response ?? 'PENDING')) onChange(option.value);
        },
      }))}
    />
  );
}
